import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { request } from '@waago/api'
import { BecomeProvider } from './BecomeProvider'

/**
 * Keeps Waago Pro to prestataires.
 *
 * <p>Sign-in is the platform's, not this app's: one phone number, one account,
 * one password everywhere. So an account that only ever bought services can
 * sign in here — correctly, because this is where it opens its prestataire
 * side — and until it does, it has no business walking around the app. UAT,
 * 2026-10-12: <em>"created a Client account with the phone, and we can log in
 * to both Client and Pro"</em>.
 *
 * <p>So every screen behind the sign-in asks this first. A client account gets
 * one screen — the offer to open its prestataire side — and nothing else:
 * no missions, no revenue, no catalogue. The moment it accepts, the whole app
 * is there, because it is then a prestataire like any other.
 *
 * <p>Gating here rather than per page is the point: the dashboard alone was
 * guarded, and the other tabs still opened on an account with nothing behind
 * them.
 */
export function ProviderGate({ children }: { children: ReactNode }) {
  const { data, isPending } = useQuery({
    queryKey: ['provider', 'summary'],
    queryFn: () => request<{ isProvider: boolean }>('/me/summary'),
    retry: false,
    staleTime: 30_000,
  })

  // Nothing is drawn before the answer: flashing a dashboard that is about to
  // be replaced reads as a bug in the app rather than as a decision.
  if (isPending) {
    return (
      <p className="animate-slide-up p-8 text-center text-sm text-slate-500" role="status">
        Chargement…
      </p>
    )
  }

  // An error leaves the app as it was: a prestataire whose connection dropped
  // must not be told they are not a prestataire.
  if (data && !data.isProvider) {
    return <BecomeProvider name={null} />
  }

  return <>{children}</>
}
